/**
 * The icon set, drawn on a 24×24 grid with a 2 stroke in currentColor.
 *
 * Ported from `legacy/frontend/src/icons.tsx` at legacy commit 2953e3f. Icons arrive with
 * the flow that needs them, in the old file's own order: six for F-000, and
 * `ChevronRightIcon` for the row F-004's list is made of, `PlusIcon` for its capsule, and
 * `CakeIcon` for the birthday card above it; `TrashIcon` came with F-005's removal and
 * this sentence did not say so until F-021's T3, and `PencilIcon` is that task's own —
 * the badge on the corner of the face on «Я» (F-021, AC-40). `ExpandIcon` (their `:126-138`,
 * its doc comment with it) came with F-007's folded code plate, on its own branch the same week.
 *
 * **None of the old file's fifteen is missing now.** `GridIcon` (`:191-198`) came with F-020's
 * door into the table by name; `DotsIcon` (`:216-224`), the «…» that F-018, F-012, F-024 and
 * F-025 each carry, with F-018's request block, the first of them built; and `GripIcon`
 * (`:200-214`), the handle a programme item is dragged by, last, with F-024's programme.
 *
 * Inline SVG rather than an icon package: sixteen shapes are smaller than any
 * library that would ship them, and these are the ones the design was drawn
 * with rather than the nearest match from somebody else's set. The stroke, the
 * caps and the joins come from `.icon` in index.css.
 *
 * One weight for the whole set, and the one exception is `PencilIcon`, which
 * DESIGN.md states rather than this file.
 * There were four - 1.5, 1.6, 1.8 and 2.0, with no rule behind them - and in
 * the rehearsal card a 1.5 clock stood directly over a 1.8 plus, so the plus
 * read as the heavier of two things that are equally secondary.
 *
 * `.icon--quiet` is for chevrons and arrows and nothing that is read as text:
 * `--icon-quiet` clears the 3:1 bar for a UI component and fails the 4.5:1 bar
 * for type.
 */
type Props = {
  size?: number;
  /** Chevrons and arrows take the quiet colour; everything else inherits. */
  quiet?: boolean;
  /**
   * A chevron that marks a fold is drawn once and turned over rather than
   * swapped for its opposite, because a substitution cannot be animated. Set
   * where the same element survives the fold; where the whole row is replaced
   * there is nothing to turn, and the pair stays two icons.
   */
  turned?: boolean;
  title?: string;
};

function Svg({
  size = 22,
  quiet = false,
  turned,
  title,
  stroke,
  box = '0 0 24 24',
  children,
}: Props & { stroke?: number; box?: string; children: React.ReactNode }) {
  return (
    <svg
      className={
        [
          'icon',
          quiet ? 'icon--quiet' : null,
          turned === undefined ? null : turned ? 'icon--turn icon--turned' : 'icon--turn',
        ]
          .filter((name) => name !== null)
          .join(' ')
      }
      width={size}
      height={size}
      viewBox={box}
      style={stroke === undefined ? undefined : { strokeWidth: stroke }}
      aria-hidden={title === undefined ? true : undefined}
      role={title === undefined ? undefined : 'img'}
    >
      {title !== undefined && <title>{title}</title>}
      {children}
    </svg>
  );
}

export function CalendarIcon(props: Props) {
  return (
    <Svg {...props}>
      <rect x="3" y="5" width="18" height="16" rx="4" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </Svg>
  );
}

/* A cake: the tray, the body, and one candle with a flame. Drawn rather than
   set as the 🎂 emoji, which arrives in whatever face the phone keeps for
   emoji and stands next to a 2 stroke looking like a sticker. */
export function CakeIcon(props: Props) {
  return (
    <Svg {...props}>
      <path d="M4 20h16" />
      <path d="M5 20v-6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6" />
      <path d="M5 15.5c1.4 0 1.4 1.4 2.8 1.4s1.4-1.4 2.8-1.4 1.4 1.4 2.8 1.4 1.4-1.4 2.8-1.4 1.4 1.4 2.8 1.4" />
      <path d="M12 12V9" />
      <path d="M12 5.5c1 1 1 2 0 2.4-1-.4-1-1.4 0-2.4Z" />
    </Svg>
  );
}

export function PeopleIcon(props: Props) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="9" r="3.5" />
      <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <circle cx="17" cy="8" r="2.6" />
      <path d="M16 13.4c2.7.3 4.5 2.2 4.5 4.8" />
    </Svg>
  );
}

export function PersonIcon(props: Props) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3.4 3.1-5.6 7-5.6s7 2.2 7 5.6" />
    </Svg>
  );
}

export function ChevronRightIcon(props: Props) {
  return (
    <Svg {...props}>
      <path d="M9 6l6 6-6 6" />
    </Svg>
  );
}

export function ChevronDownIcon(props: Props) {
  return (
    <Svg {...props}>
      <path d="M6 9l6 6 6-6" />
    </Svg>
  );
}

/**
 * Four arrows going out of the corners: what the folded code plate opens into.
 * Heavier than the rest at 2.4, because it is drawn at 14 on a 22px disc and
 * the default weight disappears at that size.
 */
export function ExpandIcon(props: Props) {
  return (
    <Svg {...props} stroke={2.4}>
      <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" />
    </Svg>
  );
}

/**
 * `cropped` cuts the grid down to the strokes, 4 to 20 instead of 0 to 24.
 *
 * The plus is drawn from 5 to 19 in a field of 24, so it carries its own air on
 * every side. Beside a word - in the schedule's capsule, where the gap is 7 and
 * the padding after it 15 - that air adds itself to both and the word looks set
 * off centre.
 */
export function PlusIcon({ cropped = false, ...props }: Props & { cropped?: boolean }) {
  return (
    <Svg {...props} box={cropped ? '4 4 16 16' : undefined}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  );
}

export function TrashIcon(props: Props) {
  return (
    <Svg {...props}>
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    </Svg>
  );
}

export function CheckIcon(props: Props) {
  return (
    <Svg {...props}>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </Svg>
  );
}

/** The one icon with a weight of its own: 10px inside a 19px badge on the
    corner of a face, where the set's 2 scaled down to 10 is a scratch. */
export function PencilIcon(props: Props) {
  return (
    <Svg {...props} stroke={2.6}>
      <path d="M15.5 4.5l4 4L8 20H4v-4z" />
    </Svg>
  );
}

export function WarningIcon(props: Props) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4.5" />
      <circle cx="12" cy="16.4" r=".7" fill="currentColor" />
    </Svg>
  );
}

export function GridIcon(props: Props) {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="16" rx="4" />
      <path d="M3 9h18M9 9v11" />
    </Svg>
  );
}

/**
 * The handle a number is dragged by: two rules on the set's own 24 grid.
 *
 * Two and not three, and no dots: at 20px a third rule closes the two gaps into
 * a smudge, and a grid of dots is a different weight from everything beside it
 * in a row - the «…» on a roster row is the only dotted glyph in the set and it
 * means something else.
 */
export function GripIcon(props: Props) {
  return (
    <Svg {...props}>
      <path d="M5 9h14M5 15h14" />
    </Svg>
  );
}

export function DotsIcon(props: Props) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="5" r=".8" fill="currentColor" />
      <circle cx="12" cy="12" r=".8" fill="currentColor" />
      <circle cx="12" cy="19" r=".8" fill="currentColor" />
    </Svg>
  );
}
